import React, { useEffect, useState } from "react";
import { MapPin, Clock, Instagram, Check, Handshake, CalendarCheck } from "lucide-react";
import { useSite } from "../context/SiteContext";
import { api, waLink, mapEmbed } from "../lib/constants";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle, GoldButton, OutlineButton } from "../components/site/Primitives";
import LeadForm from "../components/LeadForm";
import SEO from "../components/SEO";

export default function ProteseCidade({ slug }) {
  const { site } = useSite();
  const [unit, setUnit] = useState(null);

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnit(data.find((u) => u.slug === slug) || null)).catch(() => {});
  }, [slug]);

  if (!unit) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Carregando...</div>;

  const isPartner = unit.partner;
  const cityName = unit.city;

  return (
    <div>
      <SEO title={`Prótese Capilar em ${cityName} | ${isPartner ? "Prime Capilar Clinic + Arena Barbearia" : unit.name}`} description={`Avaliação, aplicação e manutenção de prótese capilar em ${cityName}. Solicite sua avaliação pelo WhatsApp.`} />

      <section className="relative min-h-[50vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={(unit.photos && unit.photos[0]) || site?.images?.clinic_scalp} alt={cityName} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-14 w-full">
          {isPartner && (
            <span className="inline-flex items-center gap-2 bg-gold text-black font-heading font-600 text-xs tracking-widest px-4 py-1.5 rounded-sm mb-4" data-testid="protese-partner-badge">
              <Handshake className="w-3.5 h-3.5" /> PARCEIRA PARA ATENDIMENTO DE PRÓTESE CAPILAR
            </span>
          )}
          {unit.slug === "garanhuns" && site?.logo_prime && (
            <img src={site.logo_prime} alt="Prime Capilar Clinic" className="w-16 h-16 lg:w-20 lg:h-20 rounded-full mb-4 border border-white/25 shadow-xl object-cover" />
          )}
          <Overline className="mb-3">Prótese capilar em {cityName}</Overline>
          <h1 className="font-heading font-700 uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            {isPartner ? "Prime Capilar Clinic + Arena Barbearia" : unit.name}
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <div>
            {isPartner ? (
              <p className="text-zinc-300 leading-relaxed">
                Em Maceió, os atendimentos de prótese capilar são realizados em parceria com a <span className="text-gold font-600">Arena Barbearia</span>.
                A parceria permite que clientes de Maceió e região tenham acesso à avaliação, aplicação e acompanhamento da prótese capilar.
              </p>
            ) : (
              <p className="text-zinc-300 leading-relaxed">{unit.tagline}. Atendimento completo de avaliação, aplicação e manutenção de prótese capilar em {cityName}.</p>
            )}

            <div className="mt-8">
              <Overline className="mb-4">Serviços disponíveis</Overline>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(unit.services || []).map((s) => (
                  <div key={s} className="flex items-center gap-2.5 text-sm text-zinc-200"><Check className="w-4 h-4 text-gold shrink-0" /> {s}</div>
                ))}
              </div>
            </div>

            <div className="mt-8 space-y-3 text-sm text-zinc-300">
              <p className="flex items-center gap-2.5"><MapPin className="w-4 h-4 text-gold" /> {unit.address}</p>
              <p className="flex items-center gap-2.5"><Clock className="w-4 h-4 text-gold" /> {unit.hours}</p>
              {unit.instagram && <a href={unit.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 hover:text-gold transition-colors"><Instagram className="w-4 h-4 text-gold" /> Instagram</a>}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {unit.slug === "arapiraca" ? (
                <>
                  <GoldButton href={waLink(site, "barbearia")} data-testid="protese-arapiraca-barbearia"><CalendarCheck className="w-4 h-4" /> AGENDAR NA BARBEARIA</GoldButton>
                  <OutlineButton href={waLink(site, "arapiraca")} data-testid="protese-arapiraca-avaliacao">FAZER AVALIAÇÃO DE PRÓTESE</OutlineButton>
                </>
              ) : (
                <GoldButton href={waLink(site, unit.wa_key)} size="lg" data-testid={`protese-${unit.slug}-avaliacao`}>
                  {unit.slug === "garanhuns" ? "AGENDAR AVALIAÇÃO EM GARANHUNS" : "QUERO FAZER UMA AVALIAÇÃO EM MACEIÓ"}
                </GoldButton>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="space-y-4">
            <div className="aspect-[4/3] overflow-hidden rounded-sm border border-white/10">
              <iframe title={`Mapa ${cityName}`} src={mapEmbed(unit.map_query)} className="w-full h-full" style={{ border: 0 }} loading="lazy" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {(unit.photos || []).slice(0, 3).map((p, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-sm border border-white/10">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-[#080808] border-t border-white/10 py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <Reveal><Overline className="mb-3 text-center">Avaliação</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle className="text-center">Solicite sua avaliação em {cityName}</SectionTitle></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border border-white/12 rounded-sm bg-[#0b0b0b] p-6 lg:p-8">
              <LeadForm paginaOrigem={`Prótese ${cityName}`} defaultCidade={cityName} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
